import { Auth } from "aws-amplify";
import { takeEvery, call, put, fork, select } from "redux-saga/effects";
import * as actions from "../../actions";
import {
  getDispatchFeedEvent,
  getDispatchFeedEventsPayload,
  getPinnedDispatchEventPayload,
  getPinnedDispatchEventsPayload,
  getTmcLogPayload,
  pinDispatchEventPayload,
  removePinDispatchEventPayload,
  types,
} from "../../actions";
import * as api from "../../api";
import { DispatchFeedStoreError, DispatchFeedStoreSuccess } from "../../enums/store-messages/dispatch";
import { IRootReducer } from "../../reducers";
import {
  DispatchEventFeedModel,
  FeedStatus,
  PagingKeys,
  PagingResponse,
  PinEvent,
  TmcLog,
} from "../../reducers/states/dispatch-event-feed-state";
import { IsResponseValidUtil } from "../../utils/api-utils";

export function* getSingleDispatchEvent(action: {
  type: typeof types.GET_DISPATCH_FEED_EVENT;
  payload: getDispatchFeedEvent;
}): Generator<any, void, unknown> {
  try {
    // Clear any previous data from appearing before the latest data loads
    yield put(actions.clearCurrentDispatchEvent());
    const result: any = yield call(api.getDispatchEvent, action.payload.id);
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        let apiResultPayload: DispatchEventFeedModel = result.data;
        yield put(actions.getDispatchFeedEventSuccess(apiResultPayload));
      }
    }
  } catch (e) {
    //TODO: Implement proper logging and error handling
    console.log(e);
  }
}
function* watchDispatchFeedEvent() {
  yield takeEvery(actions.types.GET_DISPATCH_FEED_EVENT, getSingleDispatchEvent);
}

export function* getDispatchFeedEvents(action: {
  type: typeof types.GET_DISPATCH_FEED_EVENTS;
  payload: getDispatchFeedEventsPayload;
}): Generator<any, void, unknown> {
  try {
    let pagingKeys = { pk1: "", sk1: "", pk2: "", sk2: "" } as PagingKeys;
    if (action.payload.pageResponse) {
      let existingPagingKeys: any = yield select(
        (state: IRootReducer) => state.dispatchEventFeedReducer.dispatchEvents.pagingKeys,
      );
      pagingKeys = existingPagingKeys;
    }
    
    const result: any = yield call(
      api.getDispatchEvents,
      action.payload.fromTimeStamp,
      action.payload.toTimeStamp,
      action.payload.showOpenDispatchesOnly,
      action.payload.region,
      action.payload.area,
      "", // Defaulting to not use page size
      pagingKeys,
    );

    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        let apiResultPayload: PagingResponse<DispatchEventFeedModel[]> = result.data;
        if (action.payload.pageResponse) {
          // If a paged response - append the new data to the existing data in the redux store
          let existingDispatchEvents = yield select(
            (state: IRootReducer) => state.dispatchEventFeedReducer.dispatchEvents.data,
          );
          yield put(
            actions.getDispatchFeedEventsSuccess({
              pagingKeys: apiResultPayload.pagingKeys,
              data: [...apiResultPayload.data, ...(existingDispatchEvents as DispatchEventFeedModel[])],
            }),
          );
          if (action.payload.setGeoData) {
            yield put(actions.setDispatchAPIGeoRequest(apiResultPayload.data));
          }
        } else {
          // Otherwise supply the normal API response
          yield put(actions.getDispatchFeedEventsSuccess(apiResultPayload));
          if (action.payload.setGeoData) {
            yield put(actions.setDispatchAPIGeoRequest(apiResultPayload.data));
          }
        }
      } else {
        yield put(actions.getDispatchFeedEventsFailure(DispatchFeedStoreError.DISPATCH_EVENT_FEED_GET));
      }
    } else {
      yield put(actions.getDispatchFeedEventsFailure(DispatchFeedStoreError.DISPATCH_EVENT_FEED_GET));
    }
  } catch (e) {
    yield put(actions.getDispatchFeedEventsFailure(DispatchFeedStoreError.DISPATCH_EVENT_FEED_GET));
  }
}
function* watchDispatchFeedEvents() {
  yield takeEvery(actions.types.GET_DISPATCH_FEED_EVENTS, getDispatchFeedEvents);
}

export function* getNoteworthyEvents(): Generator<any, void, unknown> {
  try {
    const result: any = yield call(api.getNoteworthyEvents);
    if (result && result.status === 200 && IsResponseValidUtil(result.data)) {
      // Review: Move this filter and a sort into the API Lambda
      let apiResultPayload: DispatchEventFeedModel[] = result.data;
      let onlyOpen = yield select((state: IRootReducer) => state.userInfoReducer.userPreferences.showOpenDispatchesOnly);
      if (onlyOpen) {
        let openEvents = apiResultPayload.filter((r) => r.status === "OPEN");
        yield put(
          actions.getNoteworthyEventsSuccess(
            [...openEvents].sort((d1, d2) => new Date(d2.lastUpdateDt).getTime() - new Date(d1.lastUpdateDt).getTime()),
          ),
        );
      } else {
        yield put(
          actions.getNoteworthyEventsSuccess(
            [...apiResultPayload].sort(
              (d1, d2) => new Date(d2.lastUpdateDt).getTime() - new Date(d1.lastUpdateDt).getTime(),
            ),
          ),
        );
      }
    } else {
      yield put(actions.getNoteworthyEventsFailure(DispatchFeedStoreError.NOTEWORTHY_EVENT_FEED_GET));
    }
  } catch (e) {
    yield put(actions.getNoteworthyEventsFailure(DispatchFeedStoreError.NOTEWORTHY_EVENT_FEED_GET));
  }
}
function* watchGetNoteworthyEvents() {
  yield takeEvery(actions.types.GET_NOTEWORTHY_DISPATCH_EVENTS, getNoteworthyEvents);
}

export function* getTmcLog(action: {
  type: typeof types.GET_TMC_LOG;
  payload: getTmcLogPayload;
}): Generator<any, void, unknown> {
  try {
    // Clear the current TMCLog loaded in
    yield put(actions.clearCurrentTmcLog());
    const result: any = yield call(api.getTmcLog, action.payload.wspId);
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        let apiResultPayload: TmcLog = result.data;
        yield put(actions.getTmcLogSuccess(apiResultPayload));
      }
    } else {
      yield put(actions.getTmcLogFailure(DispatchFeedStoreError.TMC_LOG_GET));
    }
  } catch (e) {
    yield put(actions.getTmcLogFailure(DispatchFeedStoreError.TMC_LOG_GET));
  }
}
function* watchGetTmcLog() {
  yield takeEvery(actions.types.GET_TMC_LOG, getTmcLog);
}

export function* getPinnedDispatchEvents(action: {
  type: typeof types.GET_PINNED_DISPATCH_EVENTS;
  payload: getPinnedDispatchEventsPayload;
}): Generator<any, void, unknown> {
  try {
    const currentUser: any = yield Auth.currentSession();
    const email = currentUser.getIdToken().payload["custom:vccEmail"];
    const result: any = yield call(
      api.getPinnedDispatchEvents,
      email,
      action.payload.fromTimeStamp,
      action.payload.toTimeStamp,
    );
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        let pinnedEvents: DispatchEventFeedModel[] = result.data;
        yield put(actions.getPinnedDispatchEventsSuccess(pinnedEvents));
      } else {
        yield put(actions.getPinnedDispatchEventsFailure(DispatchFeedStoreError.DISPATCH_EVENTS_PIN_GET));
      }
    } else {
      yield put(actions.getPinnedDispatchEventsFailure(DispatchFeedStoreError.DISPATCH_EVENTS_PIN_GET));
    }
  } catch (e) {
    yield put(actions.getPinnedDispatchEventsFailure(DispatchFeedStoreError.DISPATCH_EVENTS_PIN_GET));
  }
}
function* watchGetPinnedDispatchEvents() {
  yield takeEvery(actions.types.GET_PINNED_DISPATCH_EVENTS, getPinnedDispatchEvents);
}

export function* getPinnedDispatchEvent(action: {
  type: typeof types.GET_PINNED_DISPATCH_EVENT;
  payload: getPinnedDispatchEventPayload;
}): Generator<any, void, unknown> {
  try {
    const currentUser: any = yield Auth.currentSession();
    const email = currentUser.getIdToken().payload["custom:vccEmail"];
    const result: any = yield call(api.getPinnedDispatchEvent, email, action.payload.dispatchId);
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        let pinnedEvent: PinEvent = result.data;
        yield put(actions.getPinnedDispatchEventSuccess(pinnedEvent));
      } else {
        yield put(actions.getPinnedDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_GET));
      }
    } else {
      yield put(actions.getPinnedDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_GET));
    }
  } catch (e) {
    yield put(actions.getPinnedDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_GET));
  }
}
function* watchGetPinnedDispatchEvent() {
  yield takeEvery(actions.types.GET_PINNED_DISPATCH_EVENT, getPinnedDispatchEvent);
}

export function* pinDispatchEvents(action: {
  type: typeof types.PIN_DISPATCH_EVENT;
  payload: pinDispatchEventPayload;
}): Generator<any, void, unknown> {
  try {
    const currentUser: any = yield Auth.currentSession();
    const email = currentUser.getIdToken().payload["custom:vccEmail"];
    const result: any = yield call(api.setPinnedDispatchEvents, email, action.payload.eventsToPin);
    if (result && result.status === 200) {
      yield put(actions.pinDispatchEventSuccess(DispatchFeedStoreSuccess.DISPATCH_EVENT_PIN_SAVE));
    } else {
      yield put(actions.pinDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_SAVE));
    }
  } catch (e) {
    yield put(actions.pinDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_SAVE));
  }
}
function* watchPinDispatchEvents() {
  yield takeEvery(actions.types.PIN_DISPATCH_EVENT, pinDispatchEvents);
}

export function* removePinDispatchEvent(action: {
  type: typeof types.REMOVE_PIN_DISPATCH_EVENT;
  payload: removePinDispatchEventPayload;
}): Generator<any, void, unknown> {
  try {
    const currentUser: any = yield Auth.currentSession();
    const email = currentUser.getIdToken().payload["custom:vccEmail"];
    const result: any = yield call(api.removePinnedDispatchEvent, email, action.payload.dispatchId);
    if (result && result.status === 200) {
      yield put(actions.removePinDispatchEventSuccess(DispatchFeedStoreSuccess.DISPATCH_EVENT_PIN_REMOVE));
    } else {
      yield put(actions.removePinDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_SAVE));
    }
  } catch (e) {
    yield put(actions.removePinDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_SAVE));
  }
}
function* watchRemovePinDispatchEvent() {
  yield takeEvery(actions.types.REMOVE_PIN_DISPATCH_EVENT, removePinDispatchEvent);
}

export function* getDispatchFeedStatus(): Generator<any, void, unknown> {
  try {
    const result: any = yield call(api.getFeedStatus);
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        let apiResultPayload: FeedStatus[] = result.data;
        yield put(actions.getFeedStatusSuccess(apiResultPayload));
      }
    }
  } catch (e) {
    //TODO: Implement proper logging and error handling
    console.log(e);
  }
}
function* watchDispatchFeedStatus() {
  yield takeEvery(actions.types.GET_DISPATCH_FEED_STATUS, getDispatchFeedStatus);
}

const dispatchEventFeedSagas = [
  fork(watchDispatchFeedEvent),
  fork(watchDispatchFeedEvents),
  fork(watchGetNoteworthyEvents),
  fork(watchGetTmcLog),
  fork(watchPinDispatchEvents),
  fork(watchRemovePinDispatchEvent),
  fork(watchGetPinnedDispatchEvent),
  fork(watchGetPinnedDispatchEvents),
  fork(watchDispatchFeedStatus),
];

export default dispatchEventFeedSagas;
